import { useState } from 'react';
import { createPortal } from 'react-dom';
import { LazyImage } from '../../components/LazyImage';
import { pagePreviewSrc } from '../../lib/pagePreviewSrc';

const W = 288;
const H = 360;
const GAP = 12;

// Floating thumbnail of a reconstructed document's first constituent page,
// shown while hovering the document row. Mirrors PagePreviewPopover's
// flip-left / clamp-on-screen positioning; pointer-events-none so it never
// steals the hover. No-op wrapper when the document has no page id.
export function DocumentThumbnailHover({
  pageId,
  children,
}: {
  pageId: string | null;
  children: React.ReactNode;
}) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);

  if (!pageId) return <>{children}</>;

  return (
    <span
      className="inline-block"
      onMouseEnter={(e) => setAnchor(e.currentTarget.getBoundingClientRect())}
      onMouseLeave={() => setAnchor(null)}
    >
      {children}
      {anchor && <ThumbnailPopover pageId={pageId} anchor={anchor} />}
    </span>
  );
}

function ThumbnailPopover({ pageId, anchor }: { pageId: string; anchor: DOMRect }) {
  const flipLeft = anchor.right + GAP + W > window.innerWidth;
  const left = flipLeft ? Math.max(8, anchor.left - GAP - W) : anchor.right + GAP;
  const top = Math.max(8, Math.min(anchor.top, window.innerHeight - H - 8));

  return createPortal(
    <div
      className="pointer-events-none fixed z-50 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl dark:border-zinc-700 dark:bg-zinc-800"
      style={{ left, top, width: W, height: H }}
    >
      <LazyImage
        src={pagePreviewSrc(pageId, { resize: 'x400' })}
        className="h-full w-full"
        imageClassName="h-full w-full object-contain"
      />
    </div>,
    document.body,
  );
}
